<script lang="ts">
import type { HTMLButtonAttributes } from 'svelte/elements';
import type { LinkAnchorProps, LinkButtonProps, LinkProps } from '$lib/types';
import { EVENTS, trackEvent } from '$lib/utils/analytics';
import { getLinkAttributes } from '$lib/utils/url.js';

let {
    to = '',
    state = '',
    target = '',
    active = false,
    onClick,
    type = 'link',
    text,
    class: className,
    ...rest
}: LinkProps & { class?: string } = $props();

function handleClick(event: MouseEvent) {
    if (/^(mailto|tel):/.test(to)) {
        trackEvent(EVENTS.LINK_CLICKED, {
            link_type: to.startsWith('mailto:') ? 'mailto' : 'tel',
            link_url: to,
            link_text: typeof text === 'string' ? text : undefined,
            page_path: window.location.pathname,
        });
    }
    onClick?.(event);
}

const linkAttributes = $derived(
    getLinkAttributes(to, { target, rel: (rest as LinkAnchorProps).rel }),
);

// Derived props keep rest-prop forwarding reactive without triggering state capture warnings.
const buttonProps = $derived.by(() => {
    const restWithButtonProps = rest as LinkButtonProps & Pick<HTMLButtonAttributes, 'disabled'>;
    const { id, style, tabindex, title, disabled } = restWithButtonProps;
    return { id, style, disabled, tabindex, title };
});

const anchorRest = $derived.by(() => {
    const { class: _unusedClass, ...forwardedProps } = rest as LinkAnchorProps & {
        class?: string;
    };
    delete forwardedProps.rel;
    return forwardedProps;
});
</script>

{#if type === "link"}
    <a
        href={to}
        {...linkAttributes}
        class="{state} {className || ''}"
        class:active
        onclick={handleClick}
        {...anchorRest}
        >{#if typeof text === "function"}{@render text()}{:else}{text}{/if}</a
    >
{:else if type === "button"}
    <button
        class="{state} {className || ''}"
        class:active
        onclick={onClick}
        {...buttonProps}
        >{#if typeof text === "function"}{@render text()}{:else}{text}{/if}</button
    >
{/if}
